import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { get, post, put } from "../../../helper/Api_helper.js";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  VYLABS_BASE_URL,
  JOBS_LIST,
  GET_JOB_DETAILS_BY_ID,
  GET_CLIENT_DETAILS,
  GET_CLIENTS_LIST,
  SEARCH_CLIENTS,
  UPDATE_JOB,
  JOBS_SIMPLE_SEARCH,
} from "../../../helper/url_helper.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import SidePopup from "../../../Common/SidePopup/SidePopup.js";
import JobsCard, {
  JobStatusUpdatePopup,
} from "../../../Common/Card/JobsCard.js";
import DynamicSkeleton from "../../../Common/Skeleton/Skeleton.jsx";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import NojobsAnimation from "../../../Assets/Json/nodata-found-robot-animation.json";
import Button from "../../../Common/Button/Button.js";
import JobsDetailsContainer from "../../../Common/Jobs-Details-Container/JobsDetailsContainer";
import JobSimpleSearchBox from "../../../Common/Simple-Search-Box/JobSimpleSearchBox";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";

const JOBS_PAGE_SIZE = 10;

const getJobsPageFromOffset = (offsetValue = 0) =>
  Math.floor(Number(offsetValue || 0) / JOBS_PAGE_SIZE) + 1;

const assignedDetails = [
  { label: "Created By Me", value: "user_id" },
  { label: "Assigned To Me", value: "assigned_to" },
];

const EMPTY_JOB_SEARCH_PARAMS = {
  query: "",
  job_role: "",
  job_type: "",
  location: "",
  skills: "",
  min_years_experience: "",
  max_years_experience: "",
  client_id: "",
  client_contact_id: "",
  user_job_relation: "",
  priority: "",
};

function JobSelectionPopup({
  isOpen,
  onClose,
  onSelectJob,
  isDarkMode,
  actionType = "assignJob",
  actionData = {},
}) {
  const [jobs, setJobs] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const observer = useRef();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsJob, setDetailsJob] = useState(null);
  const [activeTab, setActiveTab] = useState("Job Details");
  const [assignedCount, setAssignedCount] = useState(0);
  const { setIsFromCandidateRecommendedJobs, isHiringTeamTenant, userDetails } =
    useContext(DynamicContext);
  const [jobOptionLoading, setJobOptionLoading] = useState(false);
  const [clientOptionLoading, setClientOptionLoading] = useState(false);
  const [simpleSearchClientList, setSimpleSearchClientList] = useState([]);
  const [simpleSearchClient, setSimpleSearchClient] = useState(null);
  const simpleSearchClientReqRef = useRef(0);
  const [noClientsExist, setNoClientsExist] = useState(false);

  const [originalJobData, setOriginalJobData] = useState(null);
  const [simpleSearchClientContacts, setSimpleSearchClientContacts] = useState([]);
  const [editedJobData, setEditedJobData] = useState(null);
  const [isEditingJob, setIsEditingJob] = useState(false);
  const [jobDetailsValidation, setJobDetailsValidation] = useState(null);
  const [searchParams, setSearchParams] = useState({ ...EMPTY_JOB_SEARCH_PARAMS });
  const [hasSearched, setHasSearched] = useState(false);
  const [jobSimpleSearcParams, setJobSimpleSearchParams] = useState();
  const tenantLabel = isHiringTeamTenant ? "Hiring Team" : "Client";
  const formik = useFormik({
    initialValues: {
      title: "",
      client_id: "",
      yearsOfExperience: "",
      educationLevel: "",
      company: "",
      jobLocation: "",
      jobType: "",
      skills: [],
      jobDescription: "",
      jobRole: "",
      salary_min: "",
      salary_max: "",
      tags: [],
      languageProficiency: "",
      openings: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Job Title is required"),
      jobRole: Yup.string().required("Job Role is required"),
      jobType: Yup.string().required("Job Type is required"),
      jobLocation: Yup.string().required("Job Location is required"),
      yearsOfExperience: Yup.number()
        .required("Experience is required")
        .integer("Experience must be an integer")
        .max(99, "Please enter a two digit number")
        .typeError("Experience must be a number"),
      salary_min: Yup.number(),
      salary_max: Yup.number(),
      jobDescription: Yup.string().nullable(),
      skills: Yup.mixed()
        .test(
          "is-array-or-string",
          "Skills must be an array or string",
          function (value) {
            return Array.isArray(value) || typeof value === "string";
          },
        )
        .required("Skills are required"),
      tags: Yup.array().nullable(),
      openings: Yup.number()
        .required("Number of openings is required")
        .integer("Number of openings must be an integer")
        .min(1, "Number of openings must be at least 1")
        .typeError("Number of openings must be a number"),
    }),
  });

  const lastElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + 10);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, hasMore],
  );

  const fetchJobDetails = async (jobId) => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_JOB_DETAILS_BY_ID}?id=${jobId}`,
      );
      return response?.job || null;
    } catch (err) {
      console.error("Error fetching job details:", err);
      return null;
    }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_CLIENT_DETAILS}/?id=${clientId}`,
      );
      return response?.client || null;
    } catch (err) {
      console.warn("Could not fetch client details:", err);
      return null;
    }
  };

  const fetchAllClientsList = async (inputValue = "") => {
    const requestId = ++simpleSearchClientReqRef.current;
    try {
      setClientOptionLoading(true);
      const response = inputValue
        ? await get(
            VYLABS_BASE_URL,
            `${SEARCH_CLIENTS}?company_name=${encodeURIComponent(inputValue)}`,
          )
        : await get(VYLABS_BASE_URL, `${GET_CLIENTS_LIST}`);

      if (requestId !== simpleSearchClientReqRef.current) return;
      const { clients = [] } = response || {};
      const fetchedClients = Array.isArray(clients)
        ? clients
            .filter((client) => client?.id && client?.name)
            .map((client) => ({
              label: client.name,
              value: client.id,
            }))
        : [];

      setNoClientsExist(inputValue === "" && fetchedClients.length === 0);
      setSimpleSearchClientList(fetchedClients);
    } catch (err) {
      console.error("fetchAllClientsList error", err);
      if (requestId === simpleSearchClientReqRef.current) {
        setSimpleSearchClientList([]);
      }
    } finally {
      if (requestId === simpleSearchClientReqRef.current) {
        setClientOptionLoading(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    setSimpleSearchClientContacts([]);
    if (!isOpen || !searchParams.client_id) return;

    const fetchContacts = async () => {
      try {
        const response = await get(
          VYLABS_BASE_URL,
          `${GET_CLIENT_DETAILS}?id=${encodeURIComponent(searchParams.client_id)}`,
        );
        if (cancelled) return;
        const contacts = response?.client?.contacts;
        setSimpleSearchClientContacts(
          Array.isArray(contacts)
            ? contacts
                .filter((contact) => contact?.id && contact?.name)
                .map((contact) => ({ label: contact.name, value: contact.id }))
            : [],
        );
      } catch (error) {
        console.error("Error fetching simple search client contacts:", error);
        if (!cancelled) setSimpleSearchClientContacts([]);
      }
    };

    fetchContacts();
    return () => {
      cancelled = true;
    };
  }, [isOpen, searchParams.client_id]);

  const handleSimpleSearchClientInputChange = (_, value, reason) => {
    if (reason === "reset") return;
    fetchAllClientsList(value || "");
  };

  const handleSimpleSearchClientOpen = () => {
    if (simpleSearchClientList.length === 0) {
      fetchAllClientsList("");
    }
  };

  const handleJobSelect = async (job) => {
    setSelectedJob(job);

    try {
      const detailedJob = await fetchJobDetails(job.id);
      if (detailedJob) {
        if (detailedJob?.client_id && !detailedJob?.client?.contact_person) {
          const clientDetails = await fetchClientDetails(detailedJob.client_id);
          if (clientDetails) {
            detailedJob.client = clientDetails;
          }
        }
        setSelectedJob(detailedJob);
      }
    } catch (err) {
      console.error("Error fetching detailed job info:", err);
    }
  };

  const fetchJobsList = useCallback(async (customOffset) => {
    try {
      if (customOffset === 0) {
        setIsInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const payload = {
        limit: JOBS_PAGE_SIZE,
        status: true,
        page: getJobsPageFromOffset(customOffset),
      };

      const response = await post(VYLABS_BASE_URL, JOBS_LIST, payload);
      const { jobs = [] } = response || {};
      const fetchedJobs = Array.isArray(jobs)
        ? jobs
            .filter((c) => c?.id)
            .map((job) => ({
              ...job,
              tags: job.tags || [],
              skills: job.skills || "",
              description: job.description || "",
              title: job.title || "",
              job_role: job.job_role || "",
              client_id: job.client_id || "",
            }))
        : [];

      setJobs((prev) => {
        if (customOffset === 0) {
          return fetchedJobs;
        } else {
          const existingJobs = new Map();
          (prev || []).forEach((job) => {
            if (job?.id) existingJobs.set(job.id, job);
          });
          fetchedJobs.forEach((job) => {
            if (job?.id) existingJobs.set(job.id, job);
          });
          return Array.from(existingJobs.values());
        }
      });

      setHasMore(fetchedJobs.length === JOBS_PAGE_SIZE);
    } catch (err) {
      console.error("Error in fetchJobs:", err);
      if (customOffset === 0) setJobs([]);
      setHasMore(false);
    } finally {
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if(jobSimpleSearcParams !== undefined) handleJobSimpleSearch({}, offset);
      else fetchJobsList(offset);
    }
  }, [offset, isOpen, fetchJobsList]);

  const handleJobSimpleSearch = async (param = {}, customOffset) => {
    setHasSearched(true);
    setJobOptionLoading(true);
    if(Object.keys(param).length > 0) {
      setJobs([]);
      setJobSimpleSearchParams(param);
    }
    const params = Object.keys(param).length > 0 ? param : jobSimpleSearcParams;
    try {
      const payload = {
        ...params,
        active_only: true,
        limit: JOBS_PAGE_SIZE,
        page: getJobsPageFromOffset(customOffset),
      };

      const response = await post(VYLABS_BASE_URL, JOBS_SIMPLE_SEARCH, payload);
      const data = Array.isArray(response?.jobs) ? response.jobs : [];
      const formattedJobs = data.map((job) => ({
        ...job,
        tags: job.tags || [],
        skills: job.skills || "",
        description: job.description || "",
        title: job.title || "",
        job_role: job.job_role || "",
        client_id: job.client_id || "",
      }));
      setJobs((prev) => {
        if (customOffset === 0) return formattedJobs;
  
        const existingJobs = new Map();
  
        (prev || []).forEach((job) => {
          if (job?.id) existingJobs.set(job.id, job);
        });
  
        formattedJobs.forEach((job) => {
          if (job?.id) existingJobs.set(job.id, job);
        });
  
        return Array.from(existingJobs.values());
      });
      setHasMore(response?.has_more || false);
    } catch (error) {
      console.error("Error searching jobs:", error);
      setJobs([]);
      setHasMore(false);
      Toast("error", "Failed to search jobs");
    } finally {
      setJobOptionLoading(false);
    }
  };

  useEffect(() => {
    if (detailsJob && !isEditingJob) {
      const initialValues = {
        title: detailsJob.title || "",
        yearsOfExperience: detailsJob.years_experience?.toString() || "",
        educationLevel: detailsJob.education_level || "",
        company: detailsJob.company || "",
        jobLocation: detailsJob.location || "",
        jobType: detailsJob.job_type || "",
        skills: Array.isArray(detailsJob.skills)
          ? detailsJob.skills
          : typeof detailsJob.skills === "string"
            ? [detailsJob.skills]
            : [],
        jobDescription: detailsJob.description || "",
        jobRole: detailsJob.job_role || "",
        salary_min: detailsJob.salary_min?.toString() || "",
        salary_max: detailsJob.salary_max?.toString() || "",
        tags: detailsJob.tags || [],
        languageProficiency: detailsJob.language_proficiency || "",
        openings: detailsJob.openings?.toString() || "",
      };
      formik.setValues(initialValues);
    }
  }, [detailsJob, isEditingJob]);

  const handleConfirm = async () => {
    if (selectedJob) {
      try {
        let finalJob = selectedJob;
        if (
          !selectedJob.description ||
          !selectedJob.skills ||
          !selectedJob.job_role
        ) {
          const detailedJob = await fetchJobDetails(selectedJob.id);
          if (detailedJob) {
            finalJob = detailedJob;
          }
        }
        onSelectJob({
          job: finalJob,
          actionType,
          actionData,
        });
      } catch (err) {
        console.error("Error confirming job selection:", err);
        onSelectJob({
          job: selectedJob,
          actionType,
          actionData,
        });
      }
    }
  };

  const resetState = () => {
    setJobs([]);
    setOffset(0);
    setHasMore(true);
    setSelectedJob(null);
    setDetailsOpen(false);
    setDetailsJob(null);

    setOriginalJobData(null);
    setEditedJobData(null);
    setIsEditingJob(false);
    setJobDetailsValidation(null);
    resetSearchFilters();
  };

  const resetSearchFilters = () => {
    setHasSearched(false);
    simpleSearchClientReqRef.current += 1;
    setClientOptionLoading(false);
    setSearchParams({ ...EMPTY_JOB_SEARCH_PARAMS });
    setSimpleSearchClient(null);
    setSimpleSearchClientContacts([]);
    setSimpleSearchClientList([]);
    setNoClientsExist(false);
    setJobSimpleSearchParams();
  };

  const handleClearSearchForm = () => {
    resetSearchFilters();
    if (offset === 0) {
      fetchJobsList(0);
    } else {
      setOffset(0);
    }
    setJobSimpleSearchParams();
  };

  const handleOpenDetails = async (job, tab) => {
    setIsFromCandidateRecommendedJobs(true);
    setDetailsOpen(true);
    setActiveTab(tab);
    try {
      setOriginalJobData(null);
      setEditedJobData(null);
      setIsEditingJob(false);
      setJobDetailsValidation(null);
      let detailedJob = job;
      const jobRes = await fetchJobDetails(job.id);
      if (jobRes) {
        detailedJob = jobRes;
        if (detailedJob.client_id && !detailedJob.client?.contact_person) {
          const clientRes = await fetchClientDetails(detailedJob.client_id);
          if (clientRes) detailedJob.client = clientRes;
        }
      }
      setDetailsJob(detailedJob);
    } catch (e) {
      console.error(e);
      setDetailsJob(job);
    }
  };

  const handleCloseDetails = () => {
    if (isEditingJob) {
      resetJobEdit();
    }
    setIsFromCandidateRecommendedJobs(false);
    setDetailsOpen(false);
    setDetailsJob(null);
    setActiveTab("Job Details");
  };

  const handleEditJob = () => {
    if (!detailsJob?.id) return;
    setOriginalJobData({ ...detailsJob });
    setEditedJobData({ ...detailsJob });
    setIsEditingJob(true);
  };

  const resetJobEdit = () => {
    if (originalJobData) {
      setDetailsJob(originalJobData);
      formik.setValues({
        title: originalJobData.title || "",
        yearsOfExperience: originalJobData.years_experience?.toString() || "",
        educationLevel: originalJobData.education_level || "",
        company: originalJobData.company || "",
        jobLocation: originalJobData.location || "",
        jobType: originalJobData.job_type || "",
        skills: Array.isArray(originalJobData.skills)
          ? originalJobData.skills
          : typeof originalJobData.skills === "string"
            ? [originalJobData.skills]
            : [],
        jobDescription: originalJobData.description || "",
        jobRole: originalJobData.job_role || "",
        salary_min: originalJobData.salary_min?.toString() || "",
        salary_max: originalJobData.salary_max?.toString() || "",
        tags: originalJobData.tags || [],
        clientID: originalJobData.clientID || "",
        languageProficiency: originalJobData.language_proficiency || "",
        openings: originalJobData.openings?.toString() || "",
      });
    }
    setIsEditingJob(false);
    setOriginalJobData(null);
    setEditedJobData(null);
    setJobDetailsValidation(null);
  };

  const handleJobChange = (data) => {
    if (data.validateForm) {
      setJobDetailsValidation(() => data.validateForm);
    } else {
      const updatedValues = {
        ...formik.values,
        title: data.title || formik.values.title,
        jobRole: data.job_role || formik.values.jobRole,
        jobDescription: data.description || formik.values.jobDescription,
        skills: data.skills || formik.values.skills,
        tags: data.tags || formik.values.tags,
        clientID: data.clientID || formik.values.clientID,
        openings: data.openings || formik.values.openings,
      };
      formik.setValues(updatedValues);
      setEditedJobData((prev) => ({
        ...(prev || detailsJob || {}),
        ...data,
        years_experience: formik.values.yearsOfExperience,
        location: formik.values.jobLocation,
        salary_min: formik.values.salary_min,
        salary_max: formik.values.salary_max,
        job_type: formik.values.jobType,
        openings: formik.values.openings,
      }));
    }
  };

  const saveJobChanges = async () => {
    if (!detailsJob?.id) return;

    if (jobDetailsValidation) {
      const jobDetailsResult = await jobDetailsValidation();
      if (!jobDetailsResult.isValid) {
        Toast("error", "Please fill all required fields correctly");
        return;
      }
    }

    await formik.validateForm();
    if (!formik.isValid) {
      formik.setTouched({
        title: true,
        jobRole: true,
        jobType: true,
        jobLocation: true,
        yearsOfExperience: true,
        salary_min: true,
        salary_max: true,
        skills: true,
        openings: true,
      });
      Toast("error", "Please fill all required fields correctly");
      return;
    }

    try {
      const jobDetailsResult = jobDetailsValidation
        ? await jobDetailsValidation()
        : { values: {} };
      const mainFormValues = formik.values;

      const combinedValues = {
        ...mainFormValues,
        title: mainFormValues.title || jobDetailsResult.values.title,
        jobRole: mainFormValues.jobRole || jobDetailsResult.values.job_role,
        jobDescription:
          jobDetailsResult.values.description ||
          mainFormValues.jobDescription ||
          "",
        skills: mainFormValues.skills || jobDetailsResult.values.skills,
        tags: mainFormValues.tags || jobDetailsResult.values.tags,
        openings: mainFormValues.openings || jobDetailsResult.values.openings,
      };

      const skills = Array.isArray(combinedValues.skills)
        ? combinedValues.skills.join(", ")
        : combinedValues.skills || "";

      const tags = Array.isArray(combinedValues.tags)
        ? combinedValues.tags.join(", ")
        : combinedValues.tags || "";

      const jobData = {
        jobID: detailsJob.id,
        jobTitle: combinedValues.title,
        jobRole: combinedValues.jobRole,
        jobType: combinedValues.jobType,
        jobYearsExperience: Number(combinedValues.yearsOfExperience || 0),
        jobSalaryMin: Number(combinedValues.salary_min || 0),
        jobSalaryMax: Number(combinedValues.salary_max || 0),
        jobLocation: combinedValues.jobLocation,
        jobDescription: combinedValues.jobDescription,
        jobSkills: skills,
        jobTags: tags,
        jobOpenings: Number(combinedValues.openings || 0),
      };

      await put(VYLABS_BASE_URL, UPDATE_JOB, jobData);

      const updatedJob = {
        ...detailsJob,
        id: detailsJob.id,
        title: combinedValues.title,
        job_role: combinedValues.jobRole,
        job_type: combinedValues.jobType,
        years_experience: combinedValues.yearsOfExperience,
        salary_min: combinedValues.salary_min,
        salary_max: combinedValues.salary_max,
        location: combinedValues.jobLocation,
        description: combinedValues.jobDescription,
        skills: combinedValues.skills,
        tags: combinedValues.tags,
        openings: combinedValues.openings,
      };

      setDetailsJob(updatedJob);
      setIsEditingJob(false);
      setOriginalJobData(null);
      setEditedJobData(null);
      setJobDetailsValidation(null);
      Toast("success", "Job updated successfully");

      setJobs((prevJobs) =>
        (prevJobs || []).map((job) =>
          job.id === updatedJob.id ? updatedJob : job,
        ),
      );

      setSelectedJob((prev) =>
        prev?.id === updatedJob.id ? updatedJob : prev,
      );

      handleCloseDetails();
    } catch (error) {
      console.error("Error updating job:", error);
      Toast(
        "error",
        "Failed to update job: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  useEffect(() => {
    if (isOpen) {
      setJobs([]);
      setOffset(0);
      setHasMore(true);
      setSelectedJob(null);

      resetSearchFilters();
    }
  }, [isOpen]);

  return (
    <SidePopup
      isOpen={isOpen}
      onClose={() => {
        resetState();
        onClose();
      }}
      title={
        actionType === "clientEmail"
          ? `Email ${tenantLabel} for selected candidates`
          : "Assign job for selected candidates"
      }
      width="var(--side-popup-w)"
      headerRightComponent={
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            label="Select Job"
            event={handleConfirm}
            disabled={!selectedJob}
            style={{
              backgroundColor: selectedJob
                ? "#6860ee"
                : "var(--disabled-button)",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: selectedJob ? "pointer" : "not-allowed",
              height: "48px",
              marginBottom: "5px",
            }}
          />
        </div>
      }
    >
      <JobStatusUpdatePopup />
      <div style={{ backgroundColor: "var(--body__background)" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: "var(--body__background)",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "var(--body__background)",
              paddingTop: "20px",
              paddingBottom: "20px",
            }}
          >
            <JobSimpleSearchBox
              queryValue={searchParams.query}
              onQueryChange={(e) =>
                setSearchParams({ ...searchParams, query: e.target.value })
              }
              jobRoleValue={searchParams.job_role}
              onJobRoleChange={(e) =>
                setSearchParams({ ...searchParams, job_role: e.target.value })
              }
              jobTypeValue={searchParams.job_type}
              onJobTypeChange={(e) =>
                setSearchParams({ ...searchParams, job_type: e.target.value })
              }
              locationValue={searchParams.location}
              onLocationChange={(e) =>
                setSearchParams({ ...searchParams, location: e.target.value })
              }
              skillsValue={searchParams.skills}
              onSkillsChange={(e) =>
                setSearchParams({ ...searchParams, skills: e.target.value })
              }
              priorityValue={searchParams.priority}
              onPriorityChange={(e) =>
                setSearchParams((prev) => ({ ...prev, priority: e.target.value }))
              }
              minYearsExperienceValue={searchParams.min_years_experience}
              onMinYearsExperienceChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  min_years_experience: e.target.value,
                })
              }
              maxYearsExperienceValue={searchParams.max_years_experience}
              onMaxYearsExperienceChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  max_years_experience: e.target.value,
                })
              }
              clientKey={`autocomplete-client-${
                searchParams.client_id || "empty"
              }`}
              clientValue={
                searchParams.client_id
                  ? simpleSearchClient?.value === searchParams.client_id
                    ? simpleSearchClient
                    : simpleSearchClientList.find(
                      (client) => client.value === searchParams.client_id,
                    ) || null
                  : null
              }
              onClientValueChange={(selectedClient) => {
                setSimpleSearchClient(selectedClient || null);
                setSimpleSearchClientContacts([]);
                setSearchParams((prev) => ({
                  ...prev,
                  client_id: selectedClient?.value || "",
                  client_contact_id: "",
                }));

                if (!selectedClient) {
                  setSimpleSearchClientList([]);
                }
              }}
              clientContactValue={searchParams.client_contact_id}
              onClientContactChange={(e) =>
                setSearchParams((prev) => ({
                  ...prev,
                  client_contact_id: e.target.value,
                }))
              }
              clientContactOptions={simpleSearchClientContacts}
              userJobRelationValue={searchParams.user_job_relation}
              onUserJobRelationChange={(e) =>
                setSearchParams((prev) => ({
                  ...prev,
                  user_job_relation: e.target.value,
                }))
              }
              currentUserId={userDetails?.id || ""}
              clientOnInputChange={handleSimpleSearchClientInputChange}
              clientOnOpen={handleSimpleSearchClientOpen}
              clientNoOptionsText={
                noClientsExist ? "No clients..." : "No options..."
              }
              clientOptions={simpleSearchClientList}
              clientLoading={clientOptionLoading}
              onSubmit={handleJobSimpleSearch}
              onClearForm={handleClearSearchForm}
              hasSearched={hasSearched}
              isSimpleSearchLoading={jobOptionLoading}
              recommendedSearchBoxTitle="Job Search"
              subText="Find the perfect job opportunity"
              advancedFiltersInSidePopup
              userAssignedTo={assignedDetails}
            />
          </div>
        </div>
        {isInitialLoading || jobOptionLoading ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            {Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                style={{
                  borderRadius: "12px",
                  border: "1px solid var(--border__color)",
                  background: "var(--body__background)",
                }}
              >
                <DynamicSkeleton
                  variant="rectangular"
                  dimensions={{
                    width: "100%",
                    height: "180px",
                    sx: {
                      borderRadius: "16px",
                    },
                  }}
                />
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div
            role="status"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--body_color)",
            }}
          >
            <h3
              style={{
                textAlign: "center",
                color: "#6665ea",
              }}
            >
              No jobs found matching your search criteria. Try adjusting your filters.
            </h3>
            <AnimationPlayer
              animationSrc={NojobsAnimation}
              playerHeight="300px"
              playerWidth="300px"
            />
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              {jobs.map((job, index) => {
                const isLast = index === jobs.length - 1;
                const isSelected = selectedJob?.id === job.id;
                const jobData = {
                  ...job,
                  ...(isSelected && selectedJob ? selectedJob : {}),
                };

                return (
                  <div
                    key={job.id}
                    ref={isLast ? lastElementRef : null}
                    onClick={() => handleJobSelect(job)}
                    style={{
                      cursor: "pointer",
                      border: isSelected
                        ? "2px solid #6860ee"
                        : "1px solid var(--border__color)",
                      borderRadius: "12px",
                      padding: "10px",
                      backgroundColor: isSelected
                        ? "rgba(104, 96, 238, 0.1)"
                        : "var(--body__background)",
                    }}
                  >
                    <JobsCard
                      jobId={jobData.id}
                      title={jobData.title}
                      client={jobData.client?.name || jobData.client_name}
                      location={jobData.location}
                      jobType={jobData.job_type}
                      salaryRange={`${jobData.salary_min || 0} - ${jobData.salary_max || 0}`}
                      jobDescription={jobData.description}
                      skills={jobData.skills}
                      openings={jobData.openings}
                      jobRole={jobData.job_role}
                      isDarkMode={isDarkMode}
                      cardStyle={{ cursor: "pointer" }}
                      experience={jobData.years_experience}
                      minSalary={jobData.salary_min}
                      maxSalary={jobData.salary_max}
                      isActive={jobData.active}
                      client_contact_person={
                        jobData?.client_contact
                          ? jobData?.client_contact?.name
                          : jobData?.client?.contact_person
                      }
                      actionText={true}
                      viewJobEventHandler={(e) => {
                        e.stopPropagation();
                        handleOpenDetails(jobData, "Job Details");
                      }}
                      assignCandidateEventHandler={(e) => {
                        e.stopPropagation();
                        handleOpenDetails(jobData, "Assigned Candidates");
                      }}
                    />
                  </div>
                );
              })}
            </div>
            {isLoadingMore && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderRadius: "12px",
                      border: "1px solid var(--border__color)",
                      background: "var(--body__background)",
                    }}
                  >
                    <DynamicSkeleton
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: "180px",
                        sx: {
                          borderRadius: "16px",
                        },
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {detailsJob && (
        <JobsDetailsContainer
          isOpen={detailsOpen}
          onClose={handleCloseDetails}
          selectedJob={detailsJob}
          isEditingJob={isEditingJob}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          formik={formik}
          editedJobData={editedJobData}
          handleJobChange={handleJobChange}
          jobId={detailsJob?.id}
          assignedCount={assignedCount}
          setAssignedCount={setAssignedCount}
          onAssignedCountChange={setAssignedCount}
          showRecommendedCandidatesTab={false}
          handleEditJob={handleEditJob}
          saveJobChanges={saveJobChanges}
          resetJobEdit={resetJobEdit}
        />
      )}
    </SidePopup>
  );
}

export default JobSelectionPopup;
